//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { CampaignWorkflowAffiliateConfigurationEnum } from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import { CreatorsCampaignsTrackingIds } from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTrackingId';

// Tooltips section
export const CREATORS_CAMPAIGN_UPDATE_BASICS_TOOLTIPS = {
  DESCRIPTION: 'Enter a description for your campaign.',
  TITLE: 'Enter the campaign title you want to display to creators.',
  IMAGES: 'Upload the image you want to include in your campaign.',
};

export const CREATORS_CAMPAIGN_UPDATE_AFFILIATES_TOOLTIPS = {
  GOOGLE_ANALYTICS_DESTINATION_URL:
    'To create and track affiliate links in Google Analytics, please enter the destination page URL of your e-commerce store where you want people to visit.',
  SHOPIFY_DISCOUNT_CODE:
    'Discount code groups were created in your connected e-commerce store. Select a code group to generate a unique affiliate code for each creator.',
  COMMISSION_TYPE:
    "This is the commission amount you'll give affiliates for revenue made using their code. You can choose to pay a percentage or a fixed amount for each sale.",
  AFFILIATE_PARTNER: 'Select your preferred affiliate tracking partner.',
  SHOPIFY_INTEGRATION_REQUIRED:
    'Shopify integration required. Connect your Shopify store in My Account to enable affiliate tracking and commission management.',
  GA_ECOMMERCE_TRACKING_REQUIRED:
    "Your Google Analytics account is connected, but we can't track affiliate conversions. Please ensure E-commerce Tracking is enabled in your GA settings.",
  GA_TRACKING_PARAMETERS_REQUIRED:
    "Your Google Analytics account is connected, but we can't properly attribute affiliate traffic. Please configure your GA tracking parameters in your Vibe settings.",
  ACCOUNT_CONNECTION_REQUIRED:
    'Connect your account to enable affiliate tracking and conversion analytics.',
  IMPACT_PROGRAM:
    'Select the Impact program you want to link to this campaign for affiliate tracking and commission management.',
  IMPACT_INTEGRATION_REQUIRED:
    'Impact.com integration is required. Please connect your Impact account in Account Settings.',
  IMPACT_PARTNER_DESCRIPTION:
    'Select Impact.com for affiliate tracking and commission management.',
};

export const CREATORS_CAMPAIGN_PAYMENT_FORM_TEXT = {
  COLLECT_PAYMENT_DETAILS_QUESTION:
    'How do you want to manage creator payments and taxes for this campaign?',
  VIBE_PAYMENT_OPTION_TITLE: 'Through Bazaarvoice Vibe',
  VIBE_PAYMENT_OPTION_DESCRIPTION:
    "We'll collect payment and tax details from creators, enabling you to pay seamlessly through the platform.",
  EXTERNAL_PAYMENT_OPTION_TITLE: 'Managed externally by your team',
  EXTERNAL_PAYMENT_OPTION_DESCRIPTION:
    "You'll handle payments and tax compliance outside the platform, and we won't collect this information from creators.",
  PAYMENT_INFO_ALERT:
    "You won't be able to initiate payment through Vibe unless you collect their payment and tax details.",
  FIXED_PAY_LABEL: 'Fixed Pay',
  OPENING_OFFER_LABEL: 'Opening Offer Value',
  ALLOW_COUNTER_OFFER_LABEL: 'Allow creators to counter your offer.',
  AFFILIATE_CONFIGURATION_LABEL: 'Affiliate Configuration',
  NO_ECOMMERCE_STORE_MESSAGE:
    "We didn't find any e-commerce store connections.",
  CONNECTED_ECOMMERCE_STORE_MESSAGE: 'We found a connected e-commerce store,',
  DISCOUNT_CODE_PLACEHOLDER:
    'Select a discount code from your e-commerce store.',
  DISCOUNT_CODE_SEARCH_PLACEHOLDER: 'Search for a discount code',
  AUTO_GENERATED_CODE_INFO:
    'A unique code and link will be auto-generated for every creator, except when a code is manually set.',
  RECOMMENDED_BADGE_TEXT: 'Recommended',
  NEGOTIATION_YES: 'Yes',
  NEGOTIATION_NO: 'No',
  DISCOUNT_CODE_LABEL: 'Discount Code',
  ADD_DESTINATION_URL_LABEL: 'Add Destination URL',
  AFFILIATE_PARTNER_SELECTION_INFO:
    'Please ensure you select the correct partner before proceeding. As it cannot be changed after you save.',
};

export const CREATORS_CAMPAIGN_PAYMENT_FORM_TOOLTIPS = {
  VIBE_PAYMENT_HELP:
    "For a smoother payment process, select this option before inviting creators to your campaign. If selected afterward, you'll need to manually send payment setup links to creators who were already invited.",
  FIXED_PAY_HELP:
    'Select this option if you want to offer creators a fixed amount for their content.',
  OPENING_OFFER_HELP: 'The initial offer made to a creator.',
  ALLOW_NEGOTIATION_HELP:
    'Select "Yes" if you want to allow creators to negotiate with a counter offer.',
  AFFILIATE_CONFIGURATION_HELP:
    'Select this option if you want to offer performance-based payment with an affiliate link or code.',
  AFFILIATE_ECOMMERCE_CONFIGURATION_HELP:
    'If e-commerce is connected, select this option to offer discount code/deals/programs commission-based payments to creators.',
};

export const CREATORS_CAMPAIGN_PAYMENT_SECTION_TEXT =
  'Set up how you want to make and negotiate payments with creators.';

export const CREATORS_CAMPAIGN_VISIBILITY_OPTIONS = {
  VISIBLE_TO_CREATORS: 'Visible To Participants',
};

export const DEFAULT_SUMMARY_EMPTY_VALUE = 'No information provided';

export const CREATORS_CAMPAIGN_TABLES_DEFAULT_PAGINATION_OPTIONS = [
  8, 16, 24, 32,
];
export const CREATORS_CAMPAIGN_TABLES_DEFAULT_PAGE_SIZE = 8;

export const CREATORS_CAMPAIGN_AFFILIATES_PERCENTAGE_COMMISSION_TYPE_ID =
  'percentage-commission-type';
export const CREATORS_CAMPAIGN_AFFILIATES_FIXED_COMMISSION_TYPE_ID =
  'fixed-commission-type';

export const CREATOR_CAMPAIGN_CONFIGURATION_DEFAULT_CONTENT_RIGHTS = `Creator grants the Brand a worldwide, irrevocable, royalty-free, and transferable right and license to reproduce, publish, distribute, display, repost, share and edit all content created by the Creator for the Brand during the duration of this Campaign. The brand can use the Creator Content for the following purposes:

- Post to social media platforms
- Run paid ads using Influencer's social media posts
- Publish the content to the Brand's website
- Use the content in the Brand's digital, email or text message communications`;

export const CREATOR_CAMPAIGN_CONFIGURATION_BAZAARVOICE_CONTENT_RIGHTS = `By submitting your proposal, you agree to our terms and conditions at: https://bv-url.com/vc-tou`;

interface Field {
  helpText?: string;
  name: string;
}

export const PayCreatorTableFields: Field[] = [
  {
    name: 'CREATORS',
  },
  {
    name: 'PENDING',
  },
  {
    name: 'PAY',
    helpText: 'Total amount to pay this creator as part of this campaign.',
  },
] as const;

export const PaymentReviewTableFields: Field[] = [
  {
    name: 'CREATORS',
  },
  {
    name: 'PAYMENT METHOD',
  },
  {
    name: 'PENDING',
  },
  {
    name: 'PAYING',
  },
] as const;

export const PAY_CREATOR_MODAL_ONLINE_PAYMENT_HTML_ID = `${CreatorsCampaignsTrackingIds.PAY_CREATOR_ONLINE_OPTION}-html-id`;

export const PAY_CREATOR_MODAL_OFFLINE_PAYMENT_HTML_ID = `${CreatorsCampaignsTrackingIds.PAY_CREATOR_OFFLINE_OPTION}-html-id`;

export const PaymentFailedTableFields: Field[] = [
  {
    name: 'CREATOR HANDLE',
  },
  {
    name: 'AMOUNT',
  },
  {
    name: 'ISSUE SUMMARY',
  },
];

export const AFFILIATE_PARTNER_OPTIONS = [
  {
    label: 'Shopify',
    value: CampaignWorkflowAffiliateConfigurationEnum.Shopify,
  },
  {
    label: 'Google Analytics',
    value: CampaignWorkflowAffiliateConfigurationEnum.GoogleAnalytics,
  },
  {
    label: 'Impact.com',
    value: CampaignWorkflowAffiliateConfigurationEnum.Impact,
  },
] as const;

export const BV_PARTNERSHIPS_EMAIL_ID = 'partnerships@bazaarvoice.com';

export const DEFAULT_ALL_SELECTION = 'All';

export const IMPACT_PARTNER_ALREADY_LINKED_WARNING_MESSAGE =
  'This Impact Partner ID is already linked to another creator. Saving these changes will unlink the previous creator and create a new mapping for this creator instead.';
